import { Dexie } from 'dexie'
import { Timecard } from '../models/timecard'
import { TimecardEvent } from '../models/timecard-event'
import { TimecardMetadata } from '../models/timecard-metadata'
import Logger from '../utils/logger'
import { StorageTimecard } from '../models/storage-timecard'
import { StorageTimecardEvent } from '../models/storage-timecard-event'

export class PersistenceService {
  timecards!: Dexie.Table<StorageTimecard, string>
  events!: Dexie.Table<StorageTimecardEvent, number>

  constructor (private readonly logger: Logger = new Logger('PersistenceService')) {
    const db = new Dexie("CoachbotExtension")
    db.version(1).stores({
      timecards: 'id, json',
      events: '++id, timecardId, json'
    })
    this.timecards = db.table('timecards')
    this.events = db.table('events')
  }

  async mapTimecard (timecardData: StorageTimecard): Promise<Timecard> {
    const timecard = JSON.parse(timecardData.json) as Timecard
    timecard.events = []
    const events = await this.events.where('timecardId').equals(timecardData.id).toArray()
    events.forEach((event: StorageTimecardEvent) => timecard.events.push(JSON.parse(event.json) as TimecardEvent))
    return timecard
  }

  async getTimeCardById (id: number): Promise<Timecard | undefined> {
    const timecardData = await this.timecards.get({ id })
    if (timecardData?.id != null) {
      return await this.mapTimecard(timecardData)
    }
    return undefined
  }

  async createEvent (timecardDetails: TimecardMetadata, event: TimecardEvent): Promise<void> {
    const timecard = await this.getTimeCardById(timecardDetails.timecardId)
    if (timecard === undefined) {
      await this.timecards.put({
        id: Number(timecardDetails.timecardId),
        json: JSON.stringify({
          company_id: timecardDetails.companyId,
          team_id: timecardDetails.teamId,
          user_id: timecardDetails.userId,
          created_at: timecardDetails.createdAt,
          tracker_type: 'Chrome'
        } as Timecard)
      })
    }
    await this.events.put({
        timecardId: timecardDetails.timecardId,
        json: JSON.stringify(event)
      }
    )
  }

  async getAllTimecards (): Promise<Timecard[]> {
    const results = []
    const timecards = await this.timecards.toArray()
    for (const timecard of timecards) {
      results.push(await this.mapTimecard(timecard))
    }
    return results
  }

  async deleteTimecardEvents (id: number): Promise<void> {
    try {
      await this.events.where('timecardId').equals(id).delete()
    } catch (err) {
      this.logger.debug(`Error occurred deleting the events for timecard: ${id}`)
    }
  }

  async deleteTimecard (id: number): Promise<void> {
    try {
      await this.deleteTimecardEvents(id)
      await this.timecards.where('id').equals(id).delete()
    } catch (err) {
      this.logger.debug(`Error occurred deleting the timecard: ${id}`)
    }
  }
}
